import { useEffect, useRef, useState } from 'react';
import { Close } from '@react-apps/design-system';
import type { ExamPreviewClassification } from '../../domain/entities/content.entity';
import { useSetContentClassification } from '../hooks/use-content-relations';
import { getApiErrorMessage } from '../display';
import { IconButton } from '@/shared/components/icon-button';
import { SubjectSearch } from './SubjectSearch';

/**
 * Mesmo contrato do comentário: o card pergunta e manda salvar.
 *
 * `save()` **propaga** a falha em vez de devolver `false`. Devolvendo, o erro
 * morria aqui e o card só sabia "não deu" — sem o erro, o aviso dele não podia
 * dizer nem qual questão nem o que o servidor respondeu, e sobrava o toast
 * genérico da rede global. Com a exceção subindo, quem avisa é um lugar só
 * (`QuestionEditForm.save`), com o contexto todo. A mensagem inline continua
 * aqui, pelo `save.error` da mutation.
 */
export interface ClassificationHandle {
  isDirty: () => boolean;
  save: () => Promise<void>;
}
export type ClassificationRef = { current: ClassificationHandle | null };

/**
 * Classificação da questão **dentro do card em edição**.
 *
 * Era um modal aberto pelo menu do card. Virou campo do formulário pelo mesmo
 * motivo do comentário: o que se edita numa questão tem de estar num lugar só,
 * salvo por um botão só — um modal por cima do card em edição criava dois
 * contextos de edição simultâneos, cada um com seu estado sujo e sua noção de
 * "salvo".
 *
 * A questão tem **um** assunto, como na ficha: era uma lista ordenada (o
 * primeiro era a primária), mas nada lê secundária. Salvar grava
 * `subjectIds: [id]` (ou `[]`), e uma secundária legada sai junto; sem mexer
 * no campo, nada é gravado e o dado legado fica onde está.
 */
export function QuestionClassificationField({
  questionId,
  current,
  onDirtyChange,
  fieldRef,
}: {
  questionId: string;
  /** O que o preview trouxe, já ordenado (primária primeiro). */
  current: ExamPreviewClassification[];
  onDirtyChange: (dirty: boolean) => void;
  fieldRef: ClassificationRef;
}) {
  const save = useSetContentClassification(questionId);

  const initial = current[0] ?? null;
  const [selected, setSelected] = useState<{
    id: string;
    name: string | null;
  } | null>(
    initial ? { id: initial.subjectId, name: initial.subjectName } : null,
  );

  // Ressemeia quando a questão muda (o card é remontado por questão, mas a
  // lista também muda de identidade a cada refetch do preview).
  const seededFor = useRef(questionId);
  useEffect(() => {
    if (seededFor.current === questionId) return;
    seededFor.current = questionId;
    setSelected(
      initial ? { id: initial.subjectId, name: initial.subjectName } : null,
    );
  }, [questionId, initial]);

  const dirty = (selected?.id ?? null) !== (initial?.subjectId ?? null);

  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);

  const selectedName = selected?.name ?? null;

  fieldRef.current = {
    isDirty: () => dirty,
    // Sem `catch`: o erro sobe para o card avisar com contexto, e o inline
    // abaixo continua sendo desenhado pelo `save.error` da mutation.
    save: async () => {
      await save.mutateAsync({ subjectIds: selected ? [selected.id] : [] });
    },
  };

  return (
    <div className="space-y-2">
      <span className="text-(--color-text-secondary) block text-sm font-medium">
        Assunto
      </span>

      {selected ? (
        <div className="flex items-center justify-between gap-2">
          <span className="text-(--color-text-primary) text-sm">
            {selectedName ?? 'Assunto removido'}
          </span>
          <IconButton
            label={
              selectedName
                ? `Remover o assunto ${selectedName}`
                : 'Remover o assunto'
            }
            icon={<Close size="sm" />}
            onClick={() => setSelected(null)}
          />
        </div>
      ) : (
        <p className="text-(--color-text-secondary) text-sm">Sem assunto.</p>
      )}

      <SubjectSearch
        label={selected ? 'Trocar assunto' : 'Definir assunto'}
        excludeId={selected?.id}
        onPick={(subject) =>
          setSelected({ id: subject.id, name: subject.name })
        }
      />

      {save.error != null && (
        <p className="text-sm text-red-600" role="alert">
          {getApiErrorMessage(save.error, 'Falha ao classificar')}
        </p>
      )}
    </div>
  );
}
